// Story 8.5 (ADO-2014), D-15: everything a screen needs to show a cart's cut-off. One call
// feeds `CutOffPill` and `CutOffBanner` (design system):
//
//   const cutOff = useDepotCutOff(cart);
//   <CutOffPill {...cutOff} />                                   // header chip, depot card
//   <CutOffPill {...cutOff} size="large" />                      // any large placement
//   <CutOffBanner depotName={depotName} {...cutOff} />           // the cart banner
//
// - The read is shared (`useCutOffStore`): every mount for one cart costs nothing extra.
//   Story 8.4 (ADO-2013), 8.5 D-20: the cut-off is read for the cart's Trading Account (each
//   Value Centre account has its own), so the hook takes the cart, not the depot alone.
// - The countdown runs to OPEN's deadline, or PASSED's next one (D-14), corrected by the
//   server clock offset. `status` is `null` while loading, after a failed first read, once
//   a failed refetch has used up its retries (D-19), and with no depot; the components
//   render nothing for `null`, NONE, or PASSED without a next deadline.
// - Screen-reader announcements are not per pill: `SiteHeader` mounts the one
//   `CutOffAnnouncer` per screen. A screen under the site header needs nothing more.

import type { CutOffStatus } from '../api/basket.client';
import type { CartAccount } from '../api/cart-target';
import { countdownDeadline } from '../shared/cutOffDisplay';
import { useCountdown, type Countdown } from './useCountdown';
import { useCutOffStatus } from './useCutOffStatus';

export interface DepotCutOff {
  readonly status: CutOffStatus | null;
  readonly countdown: Countdown | null;
}

export function useDepotCutOff(cart: CartAccount | null): DepotCutOff {
  const view = useCutOffStatus(cart);
  const status = view?.status ?? null;
  const countdown = useCountdown(countdownDeadline(status), view?.offsetMs ?? 0);
  return { status, countdown };
}
